import { Injectable, inject } from '@angular/core';
import { Borders, Workbook } from 'exceljs';
import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { TableCell } from '../../models/lab-test';
import { RecombinationQCReportData } from '../../recombination-qc/models/recombination-qc';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFRecombinationQcRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    RecombinationQC(doc: jsPDF, marginSize: number, bcaExp: RecombinationQCReportData, wellName: string): void {
        if (bcaExp !== undefined && bcaExp !== null) {
            marginSize = 40;
            const formatedData = this._reportGeneralUtilityService.FormatTableDataCompositionQC(bcaExp.recombinationData);
            doc.addPage();
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedBold);
            let currentY = 30;
            doc.text(wellName + ' ' + reportConstants.wellStreamCalculation, marginSize, currentY);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.compositionData, marginSize, currentY + 5);
            currentY += 5;

            let tableStartY = currentY + 5;
            autoTable(doc, {
                head: [
                    [
                        {
                            content: '',
                            colSpan: 2,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        { content: reportConstants.experimentNameSeparatorOil, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.experimentNameSeparatorGas, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.calcWellstreamPDF, colSpan: 2, styles: { halign: reportConstants.center } }
                    ],
                    [
                        {
                            content: reportConstants.component,
                            colSpan: 2,
                            styles: { valign: reportConstants.middle, halign: reportConstants.center }
                        },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } },
                        { content: '' },
                        { content: reportConstants.weight, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                    ]
                ],
                body: formatedData as RowInput[],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: {
                    fontSize: 8,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    lineColor: [0, 0, 0],
                    lineWidth: 0
                },
                headStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                bodyStyles: {
                    fontSize: 8,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                columnStyles: {
                    4: { cellWidth: 1 },
                    7: { cellWidth: 1 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section, table } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else if (section === reportConstants.head && row.index != 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    else if (section === reportConstants.body) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y, x, y + h);
                        if (column.index === table.columns.length - 1) {
                            doc.line(x + w, y, x + w, y + h);
                        }
                        doc.line(x, y + h, x + w, y + h);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.5, x + w, y + h - 0.5);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                        doc.line(x, y, x + w, y);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
        }
    }

    async GenerateRecombinationQC(workbook: Workbook, worksheetName: string, bcaExp: RecombinationQCReportData, wellName: string, sampleName: string, subHeader: string) {
        try {
            const compAnalysisWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            compAnalysisWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(compAnalysisWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0, 
                fitToHeight: 0,
                scale: 65,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                },
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            compAnalysisWorksheet.mergeCells(reportConstants.a1, reportConstants.b2);
            compAnalysisWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(compAnalysisWorksheet, subHeader, reportConstants.c1, reportConstants.l2);

            compAnalysisWorksheet.getColumn(reportConstants.c).width = 12;
            compAnalysisWorksheet.getColumn(reportConstants.d).width = 12;
            compAnalysisWorksheet.getColumn(reportConstants.e).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.fChar).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.gColumn).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.h).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.i).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.j).width = 1;
            compAnalysisWorksheet.getColumn(reportConstants.k).width = 11;
            compAnalysisWorksheet.getColumn(reportConstants.l).width = 11;

            compAnalysisWorksheet.mergeCells(reportConstants.b5, reportConstants.m6);
            const textReportTitleCell = compAnalysisWorksheet.getCell(reportConstants.b5);
            textReportTitleCell.value = wellName + ' ' + sampleName + '  ' + reportConstants.wellStreamCalculation;
            textReportTitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportTitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            compAnalysisWorksheet.mergeCells(reportConstants.b7, reportConstants.l8);
            const noBottomBorder = compAnalysisWorksheet.getCell(reportConstants. b7);
            noBottomBorder.border = { top: undefined };

            const tableStartRow = 9;
            compAnalysisWorksheet.getRow(tableStartRow).height = 20;
            compAnalysisWorksheet.getRow(tableStartRow + 1).height = 20;

            compAnalysisWorksheet.mergeCells(`E${tableStartRow}`, `F${tableStartRow}`);
            const textFlashedLiquidCell = compAnalysisWorksheet.getCell(`E${tableStartRow}`);
            textFlashedLiquidCell.value = reportConstants.experimentNameSeparatorOil;
            textFlashedLiquidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedLiquidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedLiquidCell.border = borderStyle;

            const headerCellH = compAnalysisWorksheet.getCell(`G${tableStartRow}`);
            headerCellH.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`H${tableStartRow}`, `I${tableStartRow}`);
            const textFlashedGasCell = compAnalysisWorksheet.getCell(`H${tableStartRow}`);
            textFlashedGasCell.value = reportConstants.experimentNameSeparatorGas;
            textFlashedGasCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFlashedGasCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFlashedGasCell.border = borderStyle;

            const headerCellK = compAnalysisWorksheet.getCell(`J${tableStartRow}`);
            headerCellK.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`K${tableStartRow}`, `L${tableStartRow}`);
            const textWholeFluidCell = compAnalysisWorksheet.getCell(`K${tableStartRow}`);
            textWholeFluidCell.value = reportConstants.calcWellstream.replace('-', '');
            textWholeFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textWholeFluidCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textWholeFluidCell.border = borderStyle;

            compAnalysisWorksheet.mergeCells(`B${tableStartRow + 1}`, `D${tableStartRow + 1}`);
            const textComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 1}`);
            textComponentCell.value = reportConstants.component;
            textComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textComponentCell.border = borderStyle;

            const textLiquidWeightPercentCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 1}`);
            textLiquidWeightPercentCell.value = reportConstants.weight;
            textLiquidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidWeightPercentCell.border = borderStyle;

            const textLiquidMolePercentCell = compAnalysisWorksheet.getCell(`F${tableStartRow + 1}`);
            textLiquidMolePercentCell.value = reportConstants.molePercent;
            textLiquidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textLiquidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textLiquidMolePercentCell.border = borderStyle;

            const subHeaderCellH = compAnalysisWorksheet.getCell(`G${tableStartRow + 1}`);
            subHeaderCellH.border = borderStyle;

            const textGasWeightPercentCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 1}`);
            textGasWeightPercentCell.value = reportConstants.weight;
            textGasWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasWeightPercentCell.border = borderStyle;

            const textGasMolePercentCell = compAnalysisWorksheet.getCell(`I${tableStartRow + 1}`);
            textGasMolePercentCell.value = reportConstants.molePercent;
            textGasMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textGasMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textGasMolePercentCell.border = borderStyle;

            const subHeaderCellK = compAnalysisWorksheet.getCell(`J${tableStartRow + 1}`);
            subHeaderCellK.border = borderStyle;

            const textFluidWeightPercentCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 1}`);
            textFluidWeightPercentCell.value = reportConstants.weight;
            textFluidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidWeightPercentCell.border = borderStyle;

            const textFluidMolePercentCell = compAnalysisWorksheet.getCell(`L${tableStartRow + 1}`);
            textFluidMolePercentCell.value = reportConstants.molePercent;
            textFluidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            textFluidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            textFluidMolePercentCell.border = borderStyle;

            const formatedData = this._reportGeneralUtilityService.FormatTableDataCompositionQC(bcaExp.recombinationData);

            for (let i = 0; i < formatedData.length; i++) {
                const row = formatedData[i];
                const component = row[0];
                let minusCount = 0;

                const isObject = typeof component === 'object' && component !== null && 'rowSpan' in component && 'content' in component;

                if (isObject && component.rowSpan > 1) {
                    compAnalysisWorksheet.mergeCells(`B${tableStartRow + 2 + i}:B${tableStartRow + 2 + i + component.rowSpan - 1}`);
                }

                if (isObject) {
                    const textComponentCell = compAnalysisWorksheet.getCell(`B${tableStartRow + 2 + i}`);
                    textComponentCell.value = component.content;
                    textComponentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                    textComponentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    textComponentCell.border = borderStyle;
                }
                else {
                    minusCount = 1;
                }

                compAnalysisWorksheet.mergeCells(`C${tableStartRow + 2 + i}`, `D${tableStartRow + 2 + i}`);
                const textComponentNameCell = compAnalysisWorksheet.getCell(`C${tableStartRow + 2 + i}`);
                textComponentNameCell.value = this.GetCellValue(row[1 - minusCount]);
                textComponentNameCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                textComponentNameCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textComponentNameCell.border = borderStyle;

                const textLiquidWeightPercentCell = compAnalysisWorksheet.getCell(`E${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textLiquidWeightPercentCell, row[2 - minusCount]);
                textLiquidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textLiquidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textLiquidWeightPercentCell.border = borderStyle;

                const textLiquidMolePercentCell = compAnalysisWorksheet.getCell(`F${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textLiquidMolePercentCell, row[3 - minusCount]);
                textLiquidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textLiquidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textLiquidMolePercentCell.border = borderStyle;

                const textCellH = compAnalysisWorksheet.getCell(`G${tableStartRow + 2 + i}`);
                textCellH.border = borderStyle;

                const textGasWeightPercentCell = compAnalysisWorksheet.getCell(`H${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textGasWeightPercentCell, row[5 - minusCount]);
                textGasWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textGasWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textGasWeightPercentCell.border = borderStyle;

                const textGasMolePercentCell = compAnalysisWorksheet.getCell(`I${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textGasMolePercentCell, row[6 - minusCount]);
                textGasMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textGasMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textGasMolePercentCell.border = borderStyle;

                const textCellK = compAnalysisWorksheet.getCell(`J${tableStartRow + 2 + i}`);
                textCellK.border = borderStyle;

                const textFluidWeightPercentCell = compAnalysisWorksheet.getCell(`K${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textFluidWeightPercentCell, row[8 - minusCount]);
                textFluidWeightPercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textFluidWeightPercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textFluidWeightPercentCell.border = borderStyle;

                const textFluidMolePercentCell = compAnalysisWorksheet.getCell(`L${tableStartRow + 2 + i}`);
                this.SetCompositionCellValue(textFluidMolePercentCell, row[9 - minusCount]);
                textFluidMolePercentCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                textFluidMolePercentCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                textFluidMolePercentCell.border = borderStyle;
            }
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    private GetCellValue(cell: TableCell): string | number {
        if (typeof cell === 'object' && cell !== null && 'content' in cell) {
            return cell.content;
        }
        return cell ?? '';
    }

    // Applies a matching decimal numFmt to composition percentages, treating 0 as blank like the previous logic.
    private SetCompositionCellValue(cell: import('exceljs').Cell, rawCellData: TableCell): void {
        const unwrapped = this.GetCellValue(rawCellData);
        if (unwrapped !== '' && !isNaN(Number(unwrapped)) && Number(unwrapped) === 0) {
            cell.value = '';
            return;
        }
        setNumericOrTextCellValue(cell, unwrapped);
    }
}